import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useSweetAlert } from '@/composables/sweetalert';
import { EmergencyAction, EmergencyGetter } from '@/store/emergency/emergency.module';
import { EmergencySchema } from '@/store/emergency/emergency.validator';
import { Emergency } from '@/store/emergency/emergency.model';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';
import Datepicker from '@vuepic/vue-datepicker';

export default defineComponent({
  name: 'EmergencyFormComponent',
  components: {
    BaseModalComponent,
    Datepicker,
  },
  props: {
    emergencyTypeId: {
      type: Number,
    },
    provinceId: {
      type: Number,
    },
    emergencyId: {
      type: Number,
    },
  },
  setup: (props) => {
    const swal = useSweetAlert();
    const store = useStore();
    const isEditMode = computed(() => !!props.emergencyId);
    const title = computed(() => isEditMode.value ? 'แก้ไขเหตุการณ์' : 'เพิ่มเหตุการณ์');
    const emergency = computed<Emergency | undefined>(() => store.getters[EmergencyGetter.BY_ID](props.emergencyId));
    const emergencyForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      id?: number | null;
      name?: string;
      description?: string;
      emergencyDate?: Date | null;
      emergencyTypeId?: number;
      provinceId: number;
    }
    const formData = reactive<Form>({
      id: null,
      name: '',
      description: '',
      emergencyDate: null,
      emergencyTypeId: props.emergencyTypeId,
      provinceId: props.provinceId as number,
    });
    const validator = computed(() => EmergencySchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      name: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'name')),
      description: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'description')),
      emergencyDate: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'emergencyDate')),
    });
    const isValid = computed(() => !errors.name && !errors.description && !errors.emergencyDate);
    const isLoading = computed<boolean>(() => store.getters[EmergencyGetter.STATUS] === 'init' || store.getters[EmergencyGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.emergencyTypeId = props.emergencyTypeId;
        formData.provinceId = props.provinceId as number;
        if (isEditMode.value) {
          formData.id = emergency.value?.id;
          formData.name = emergency.value?.name;
          formData.description = emergency.value?.description;
          formData.emergencyDate = emergency.value?.emergencyDate;
        }
        resolve();
      })
      .then(() => emergencyForm.value?.show());
    };

    // hide modal
    const hide = () => {
      emergencyForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.name = '';
        formData.description = '';
        formData.emergencyDate = null;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(EmergencyAction.UPDATE, formData)
            .then(() => hide());
        } else {
          store.dispatch(EmergencyAction.CREATE, formData)
            .then(() => hide());
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(EmergencyAction.DELETE, formData)
              .then(() => hide());
          }
        });
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      emergencyForm,
      show,
      hide,
      submitClick,
      deleteClick,
      unsubmit,
      clearForm,
    };
  },
});
